import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Award, AlertTriangle, CheckCircle2, Sparkles, RefreshCw, History, X, Calendar, FileText, MessageSquare, Compass, Target, ListChecks, BookOpen, BrainCircuit, Library, Download, ShieldCheck, ArrowLeft, User, Building2, Clock, Hash } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../../api';
import edvergenceXLogo from '../../assets/edvergenceX2.png';
import './PerformanceReport.css';

export const normalizeFitBucket = (fb) => {
  if (!fb) return 'Needs Improvement';
  const clean = String(fb).trim().toUpperCase().replace(/_/g, ' ');
  if (clean.includes('BEST FIT') || clean.includes('STRONG CANDIDATE')) return 'Strong Candidate';
  if (clean.includes('STRETCHED') || clean.includes('INTERVIEW READY')) return 'Interview Ready';
  if (clean.includes('BEST BET') || clean.includes('ALMOST READY')) return 'Almost Ready';
  if (clean.includes('NO FIT') || clean.includes('NEEDS IMPROVEMENT') || clean.includes('NOT READY')) return 'Needs Improvement';
  return fb.replace(/_/g, ' ');
};

export const getFitBucketStyle = (fb) => {
  const norm = normalizeFitBucket(fb);
  if (norm === 'Strong Candidate') {
    return { bg: '#dcfce7', text: '#16a34a', border: '#bbf7d0', rgb: [22, 163, 74] };
  }
  if (norm === 'Interview Ready') {
    return { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe', rgb: [37, 99, 235] };
  }
  if (norm === 'Almost Ready') {
    return { bg: '#fef9c3', text: '#ca8a04', border: '#fef08a', rgb: [202, 138, 4] };
  }
  return { bg: '#fee2e2', text: '#dc2626', border: '#fecaca', rgb: [220, 38, 38] };
};

const PerformanceReport = ({ reportData, mode, loading, onRefresh, onSelectReport, showHistoryInitially = false, onCloseHistory, onCloseReport, backButtonText }) => {
  const [showHistoryModal, setShowHistoryModal] = useState(showHistoryInitially);
  const [historyReports, setHistoryReports] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [downloadState, setDownloadState] = useState(null); // null | 'downloading' | 'error'

  React.useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const res = await api.get('/student/profile/me');
        if (res.data) {
          setUserProfile(res.data);
        }
      } catch (_) {
        try {
          const authRes = await api.get('/auth/me');
          if (authRes.data) {
            setUserProfile(authRes.data.student_profile || authRes.data);
          }
        } catch (e) {}
      }
    };
    fetchUserProfile();
  }, []);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    setHistoryError(null);
    try {
      // Pass the current mode to the backend to filter history
      const res = await api.get('/ai-agents/reports/history', { 
        params: { mode: mode === 'scorer' ? undefined : mode } 
      });
      if (res.data && res.data.reports) {
        setHistoryReports(res.data.reports);
      } else {
        setHistoryReports([]);
      }
    } catch (err) {
      console.error('Failed to fetch reports history:', err);
      setHistoryError('Failed to load previous reports. Please try again.');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleOpenHistory = () => {
    setShowHistoryModal(true);
    fetchHistory();
  };

  const handleCloseHistory = () => {
    setShowHistoryModal(false);
    if (onCloseHistory) onCloseHistory();
  };

  const handleSelectHistoryReport = (rep) => {
    if (onSelectReport) {
      onSelectReport(rep);
    }
    handleCloseHistory();
  };

  React.useEffect(() => {
    if (showHistoryInitially) {
      handleOpenHistory();
    }
  }, [showHistoryInitially]);

  const downloadReportPDF = (targetReportItem) => {
    if (!targetReportItem || !targetReportItem.report) return;

    setDownloadState('downloading');

    try {
    const rep = targetReportItem.report;
    const rawMode = String(targetReportItem.mode || mode || '').toLowerCase();
    const isCounsellor = rawMode.includes('counsel') || rep.career_clarity !== undefined || Array.isArray(rep.pathways) || Array.isArray(rep.action_plan);
    const isTutor = rawMode.includes('tutor') || rep.topic_mastery !== undefined || Array.isArray(rep.key_concepts_learned);
    const itemMode = isCounsellor ? 'counsellor' : (isTutor ? 'tutor' : 'interviewer');

    const createdDate   = targetReportItem.createdAt   ? new Date(targetReportItem.createdAt)   : new Date();
    let completedDate = targetReportItem.completedAt ? new Date(targetReportItem.completedAt) : null;
    if (!completedDate || isNaN(completedDate.getTime()) || completedDate.getTime() <= createdDate.getTime()) {
      completedDate = new Date(createdDate.getTime() + 15 * 60 * 1000);
    }

    const fmtDate = (d) => d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';
    const fmtTime = (d) => d ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A';

    const sessionDate  = fmtDate(createdDate);
    const startTime    = fmtTime(createdDate);
    const endTime      = fmtTime(completedDate);

    const studentName    = targetReportItem.studentName || rep.studentName || userProfile?.full_name || userProfile?.name || 'Student Candidate';
    const institutionName = targetReportItem.institutionName || rep.institutionName || userProfile?.institution_name || userProfile?.university_name || 'EdvergenceX Academy';
    const usn            = targetReportItem.usn || rep.usn || userProfile?.usn || 'N/A';

    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    if (typeof doc.setCharSpace === 'function') doc.setCharSpace(0);
    if (typeof doc.setLineHeightFactor === 'function') doc.setLineHeightFactor(1.2);

    const pw = doc.getPageWidth();
    const ph = doc.getPageHeight();
    const mg = 40;
    const contentW = pw - mg * 2;

    // ─── BRAND COLORS ────────────────────────────────────────────────────────
    const NAVY    = [26, 10, 20];   // Deep Burgundy #1a0a14
    const BLUE    = [244, 63, 94];  // Rose Accent #f43f5e
    const GREEN   = [16, 185, 129];
    const AMBER   = [245, 158, 11];
    const RED     = [239, 68, 68];
    const PURPLE  = [139, 92, 246];
    const SLATE   = [100, 116, 139];
    const LIGHT   = [248, 250, 252];
    const BORDER  = [226, 232, 240];
    const DARK    = [15, 23, 42];
    const WHITE   = [255, 255, 255];

    const rawScore = Math.max(0, Math.min(100, Number(rep.topic_mastery ?? rep.career_clarity ?? rep.composite_score ?? rep.overall_score ?? 0) || 0));
    const titleText = itemMode === 'interviewer' ? 'Interviewer Performance Report'
                    : itemMode === 'counsellor'  ? 'AI Counsellor Guidance Report'
                    : 'AI Tutor Academic Report';

    const scoreColor = rawScore >= 80 ? GREEN : rawScore >= 60 ? AMBER : RED;
    const scoreLbl   = rawScore >= 80 ? 'EXCELLENT' : rawScore >= 60 ? 'SATISFACTORY' : 'NEEDS WORK';

    const strengths  = rep.strengths  || [];
    const weaknesses = rep.weaknesses || [];
    const layers     = rep.layers     || {};
    const qaList     = rep.qa_breakdown || [];
    const transcript = rep.transcript || null;
    const pathways   = rep.pathways   || [];
    const actionPlan = rep.action_plan || [];
    const studyRecs  = rep.study_recommendations || [];
    const knowledgeGaps = rep.knowledge_gaps || [];

    let y = 0;

    // ─── HELPERS ─────────────────────────────────────────────────────────────
    const checkPage = (needed = 60) => {
      if (y + needed > ph - 50) {
        addPageFooter();
        doc.addPage();
        addPageHeader();
        y = 90;
      }
    };

    const fillRect = (x, fy, w, h, color) => {
      doc.setFillColor(...color);
      doc.rect(x, fy, w, h, 'F');
    };

    const roundRect = (x, fy, w, h, r, color, stroke) => {
      if (stroke) {
        doc.setDrawColor(...stroke);
        doc.setFillColor(...color);
        doc.roundedRect(x, fy, w, h, r, r, 'FD');
      } else {
        doc.setFillColor(...color);
        doc.roundedRect(x, fy, w, h, r, r, 'F');
      }
    };

    const txt = (text, x, fy, size, color, style = 'normal', align = 'left') => {
      doc.setFont('helvetica', style);
      doc.setFontSize(size);
      doc.setTextColor(...color);
      if (typeof doc.setCharSpace === 'function') doc.setCharSpace(0);
      if (align && align !== 'left') {
        doc.text(String(text), x, fy, { align });
      } else {
        doc.text(String(text), x, fy);
      }
    };

    const sectionHeader = (label, color = BLUE) => {
      checkPage(45);
      fillRect(mg, y, 4, 16, color);
      txt(label, mg + 12, y + 12, 12, DARK, 'bold');
      doc.setDrawColor(...BORDER);
      doc.line(mg, y + 20, pw - mg, y + 20);
      y += 30;
    };

    // ΓöÇΓöÇΓöÇ DRAW HORIZONTAL BAR CHART ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
    const drawBarChart = (entries, labelColor) => {
      const barH   = 18;
      const gap    = 10;
      const labelW = 150;
      const barAreaW = contentW - labelW - 50;

      entries.forEach(([label, score], i) => {
        checkPage(barH + gap + 5);
        const bx = mg + labelW;
        const by = y;
        const fillW = Math.max((score / 100) * barAreaW, 2);
        const barColor = score >= 80 ? GREEN : score >= 60 ? AMBER : RED;

        roundRect(bx, by, barAreaW, barH, 4, [241, 245, 249]);
        roundRect(bx, by, fillW, barH, 4, barColor);
        txt(label.replace(/_/g, ' '), mg, by + 13, 8.5, DARK, 'normal');
        roundRect(bx + barAreaW + 6, by + 1, 38, barH - 2, 4, barColor);
        txt(`${score}`, bx + barAreaW + 25, by + 13, 9, WHITE, 'bold', 'center');
        y += barH + gap;
      });
    };

    // ΓöÇΓöÇΓöÇ DRAW DONUT SCORE CIRCLE ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
    const drawScoreDonut = (cx, cy, r, score, color) => {
      const segments = 60;
      const filled = Math.round((score / 100) * segments);
      const step = (2 * Math.PI) / segments;

      doc.setDrawColor(...BORDER);
      doc.setLineWidth(8);
      doc.circle(cx, cy, r, 'S');

      doc.setDrawColor(...color);
      doc.setLineWidth(8);
      for (let i = 0; i < filled; i++) {
        const startAngle = -Math.PI / 2 + i * step;
        const endAngle   = startAngle + step + 0.02;
        const x1 = cx + r * Math.cos(startAngle);
        const y1 = cy + r * Math.sin(startAngle);
        const x2 = cx + r * Math.cos(endAngle);
        const y2 = cy + r * Math.sin(endAngle);
        doc.line(x1, y1, x2, y2);
      }
      doc.setLineWidth(0.5);
    };

    // ΓöÇΓöÇΓöÇ PAGE HEADER / FOOTER ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
    const addPageHeader = () => {
      fillRect(0, 0, pw, 46, NAVY);
      txt(titleText, mg, 28, 11, WHITE, 'bold');
      txt(`${studentName}  |  ${sessionDate}`, pw - mg, 28, 8, [148, 163, 184], 'normal', 'right');
      fillRect(0, 46, pw, 2, BLUE);
    };

    const addPageFooter = () => {
      const pn = doc.getNumberOfPages();
      fillRect(0, ph - 30, pw, 30, [245, 248, 252]);
      doc.setDrawColor(...BORDER);
      doc.line(mg, ph - 30, pw - mg, ph - 30);
      txt(`Page ${pn}`, pw - mg, ph - 12, 7.5, SLATE, 'normal', 'right');
    };

    // ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ
    //  PAGE 1 ΓÇö COVER / EXECUTIVE SUMMARY
    // ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ
    // Dark hero header background
    fillRect(0, 0, pw, 218, NAVY);
    fillRect(0, 213, pw, 8, scoreColor);
    fillRect(0, 213, pw / 3, 8, BLUE);

    // EdvergenceX Logo ΓÇö top left (use white/light variant for visibility)
    try {
      doc.addImage(edvergenceXLogo, 'PNG', mg, 14, 120, 38);
    } catch (_) {
      txt('EdvergenceX', mg, 36, 14, WHITE, 'bold');
    }

    // Report title
    const modeLabelClean = itemMode === 'interviewer' ? 'Interviewer'
                         : itemMode.charAt(0).toUpperCase() + itemMode.slice(1);
    txt(titleText.toUpperCase(), mg, 102, 16, WHITE, 'bold');
    txt(`${modeLabelClean} Assessment`, mg, 120, 9.5, [148, 163, 184], 'normal');

    // ── Student info grid — two columns, fixed label width ─────────
    const infoY = 138;
    const valGap1 = 64;
    const valGap2 = 54;
    const col2X  = mg + 225;

    const row = (label1, val1, label2, val2, rowY) => {
      // Label 1
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(label1, mg, rowY);
      // Value 1
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(255, 255, 255);
      const str1 = String(val1 || 'N/A').length > 45 ? String(val1).substring(0, 42) + '...' : String(val1 || 'N/A');
      doc.text(str1, mg + valGap1, rowY);
      // Label 2
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(label2, col2X, rowY);
      // Value 2
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(255, 255, 255);
      const str2 = String(val2 || 'N/A').length > 20 ? String(val2).substring(0, 18) + '...' : String(val2 || 'N/A');
      doc.text(str2, col2X + valGap2, rowY);
    };

    row('Student:',     studentName,     'USN:',      usn,         infoY);
    row('Institution:', institutionName, 'Date:',     sessionDate, infoY + 18);
    row('Start Time:',  startTime,       'End Time:', endTime,     infoY + 36);

    // Score donut — top right (leave space for donut, don't overlap info)
    if (itemMode === 'interviewer') {
      const donutCX = pw - 90;
      const donutCY = 105;
      drawScoreDonut(donutCX, donutCY, 46, rawScore, scoreColor);
      txt(`${rawScore}`, donutCX, donutCY + 5, 24, scoreColor, 'bold', 'center');
      txt('/ 100', donutCX, donutCY + 18, 8, SLATE, 'normal', 'center');
      txt(scoreLbl, donutCX, donutCY + 30, 6.5, scoreColor, 'bold', 'center');
    } else {
      const badgeCX = pw - 90;
      const badgeCY = 105;
      const word1 = itemMode === 'counsellor' ? 'CAREER' : 'ACADEMIC';
      roundRect(badgeCX - 50, badgeCY - 40, 100, 80, 10, [240, 249, 255], [14, 165, 233]);
      txt(word1, badgeCX, badgeCY - 10, 10, [2, 132, 199], 'bold', 'center');
      txt('GUIDANCE', badgeCX, badgeCY + 6, 10, [2, 132, 199], 'bold', 'center');
      txt('REPORT', badgeCX, badgeCY + 22, 7.5, SLATE, 'normal', 'center');
    }

    y = 234;

    // ΓöÇΓöÇ Summary info cards row ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
    const proctoring = rep.proctoring || (targetReportItem.proctoring ? targetReportItem.proctoring : null);
    const integrityScore = proctoring?.integrity_score ?? (rep.visual_violations ? Math.max(0, 100 - rep.visual_violations * 10) : 100);
    const integrityColor = integrityScore >= 80 ? GREEN : integrityScore >= 60 ? AMBER : RED;

    const cards = itemMode === 'counsellor' ? [
      { label: 'Recommended Pathways', value: pathways.length, color: [14, 165, 233] },
      { label: 'Action Steps', value: actionPlan.length, color: [139, 92, 246] },
      { label: 'Key Strengths', value: strengths.length, color: GREEN },
      { label: 'Areas for Growth', value: weaknesses.length, color: AMBER },
    ] : [
      { label: 'Overall Score', value: `${rawScore}/100`, color: scoreColor },
      { label: 'Integrity Score', value: `${integrityScore}%`, color: integrityColor },
      { label: 'Strengths', value: strengths.length, color: GREEN },
      { label: 'Areas for Growth', value: weaknesses.length, color: AMBER },
    ];

    const cardW = (contentW - 12) / 4;
    cards.forEach((card, i) => {
      const cx = mg + i * (cardW + 4);
      roundRect(cx, y, cardW, 62, 8, LIGHT, BORDER);
      fillRect(cx, y, cardW, 4, card.color);
      txt(card.label, cx + cardW / 2, y + 20, 7.5, SLATE, 'normal', 'center');
      txt(String(card.value), cx + cardW / 2, y + 44, 18, DARK, 'bold', 'center');
    });
    y += 80;

    // ΓöÇΓöÇ Fit bucket & killer question (interviewer only) ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
    if (itemMode === 'interviewer' && rep.fit_bucket) {
      const fbNormalized = normalizeFitBucket(rep.fit_bucket);
      const fbStyle = getFitBucketStyle(rep.fit_bucket);
      roundRect(mg, y, contentW, 50, 8, LIGHT, BORDER);
      txt('FIT ASSESSMENT', mg + 14, y + 18, 7.5, SLATE, 'bold');
      txt(fbNormalized.toUpperCase(), mg + 14, y + 36, 12, fbStyle.rgb, 'bold');
      if (rep.killer_question_failed) {
        roundRect(pw - mg - 165, y + 11, 153, 28, 6, [254, 226, 226], RED);
        txt('Failed Killer Question', pw - mg - 88, y + 28, 8.5, RED, 'bold', 'center');
      } else {
        roundRect(pw - mg - 165, y + 11, 153, 28, 6, [220, 252, 231], GREEN);
        txt('Passed All Questions', pw - mg - 88, y + 28, 8.5, GREEN, 'bold', 'center');
      }
      y += 62;
    }

    // ── Overall Assessment narrative ──────────────────────────────────
    y += 4;
    const summaryText = String(rep.overall_assessment || rep.summary || rep.remarks ||
      `This ${itemMode} session evaluation has been completed. The student scored ${rawScore}/100 demonstrating ${scoreLbl.toLowerCase()} performance. Please review the detailed breakdown in the following pages.`);
    const splitSummary = doc.splitTextToSize(summaryText, contentW - 28);
    const summaryLines = (Array.isArray(splitSummary) ? splitSummary : [summaryText]).slice(0, 4);
    const summaryBoxH = 26 + summaryLines.length * 12 + 8;
    roundRect(mg, y, contentW, summaryBoxH, 8, [239, 246, 255], BLUE);
    fillRect(mg, y, 4, summaryBoxH, BLUE);
    txt('EXECUTIVE SUMMARY', mg + 14, y + 16, 8, BLUE, 'bold');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...DARK);
    if (summaryLines.length > 0) doc.text(summaryLines, mg + 14, y + 30);
    y += summaryBoxH + 16;

    addPageFooter();

    // ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ
    //  PAGE 2 ΓÇö PERFORMANCE ANALYTICS & CHARTS
    // ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ
    doc.addPage();
    addPageHeader();
    y = 65;

    txt('PERFORMANCE ANALYTICS', mg, y, 14, DARK, 'bold');
    txt('Visual breakdown of evaluation scores', mg, y + 16, 8.5, SLATE, 'normal');
    doc.setDrawColor(...BORDER);
    doc.line(mg, y + 22, pw - mg, y + 22);
    y += 34;

    // ΓöÇΓöÇ Layer Bar Charts ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
    if (Object.keys(layers).length > 0) {
      sectionHeader('Performance Layers', BLUE);

      const layerEntries = Object.entries(layers);
      drawBarChart(layerEntries.map(([k, v]) => [k, v]), BLUE);
      y += 8;
    }

    // ΓöÇΓöÇ QA Score sparkline ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
    if (qaList.length > 0) {
      checkPage(120);
      sectionHeader('Question-by-Question Scores', PURPLE);

      const qaBarH   = 14;
      const qaGap    = 10;
      const qaLabelW = 180;
      const qaBarW   = contentW - qaLabelW - 48;

      qaList.forEach((qa, i) => {
        checkPage(qaBarH + qaGap + 4);
        const sc = qa.score !== undefined ? qa.score : 0;
        const bc = sc >= 80 ? GREEN : sc >= 60 ? AMBER : RED;
        const by = y;
        const bx = mg + qaLabelW;
        const fw = Math.max((sc / 100) * qaBarW, 2);

        // Question label on left (never overlapping bar)
        const qRaw = (qa.question || `Question ${i + 1}`).trim();
        const shortQ = qRaw.length > 32 ? qRaw.substring(0, 30) + '...' : qRaw;
        txt(`Q${i + 1}: ${shortQ}`, mg, by + 10, 7.5, DARK, 'normal');

        // Progress track & fill
        roundRect(bx, by, qaBarW, qaBarH, 3, [241, 245, 249]);
        roundRect(bx, by, fw, qaBarH, 3, bc);

        // Score badge on right
        roundRect(bx + qaBarW + 6, by, 36, qaBarH, 3, bc);
        txt(`${sc}`, bx + qaBarW + 24, by + 10, 8, WHITE, 'bold', 'center');

        y += qaBarH + qaGap;
      });
      y += 8;
    }

    // ΓöÇΓöÇ Legend ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
    checkPage(36);
    roundRect(mg, y, contentW, 30, 6, LIGHT, BORDER);
    const legendItems = [['Score >= 80 : Excellent', GREEN], ['60 - 79 : Satisfactory', AMBER], ['Score < 60 : Needs Work', RED]];
    legendItems.forEach(([lbl, col], i) => {
      const lx = mg + 10 + i * 170;
      fillRect(lx, y + 11, 10, 10, col);
      txt(lbl, lx + 14, y + 20, 8, DARK, 'normal');
    });
    y += 44;

    // ΓöÇΓöÇ Proctoring & Assessment Integrity (for Interviewer mode) ΓöÇΓöÇΓöÇΓöÇΓöÇ
    if (itemMode === 'interviewer') {
      checkPage(140);
      sectionHeader('Proctoring & Assessment Integrity', [99, 102, 241]);

      const tabSwitches = proctoring?.breakdown?.tabSwitches || 0;
      const faceAbsence = proctoring?.breakdown?.faceAbsence || 0;
      const headTurns   = proctoring?.breakdown?.headTurns || 0;
      const phones      = proctoring?.breakdown?.phoneDetected || 0;
      const multiFaces  = proctoring?.breakdown?.multipleFaces || 0;

      const procHead = [['Proctoring Check', 'Observed Behavior', 'Status', 'Risk Impact']];
      const procBody = [
        ['Browser & Window Focus', `${tabSwitches} tab/window departures`, tabSwitches === 0 ? 'Verified Clean' : `${tabSwitches} Violation(s)`, tabSwitches === 0 ? 'None' : 'Moderate'],
        ['Camera & Face Presence', `${faceAbsence} frame departures`, faceAbsence === 0 ? 'Continuous Presence' : `${faceAbsence} Absence(s)`, faceAbsence === 0 ? 'None' : 'High'],
        ['Head Pose & Attention', `${headTurns} gaze shifts away`, headTurns === 0 ? 'Screen Focused' : `${headTurns} Look-away(s)`, headTurns === 0 ? 'None' : 'Low'],
        ['Device & Phone Detection', `${phones} unauthorized devices`, phones === 0 ? 'No Devices Found' : `${phones} Phone Detected`, phones === 0 ? 'None' : 'Critical'],
        ['Room & Environment Privacy', `${multiFaces} secondary people`, multiFaces === 0 ? 'Private Room' : `${multiFaces} Multi-face Detected`, multiFaces === 0 ? 'None' : 'Critical']
      ];

      autoTable(doc, {
        startY: y,
        margin: { left: mg, right: mg },
        head: procHead,
        body: procBody,
        theme: 'grid',
        headStyles: { fillColor: [99, 102, 241], textColor: 255, fontSize: 8.5, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8, textColor: [30, 41, 59] },
        styles: { cellPadding: 5 }
      });
      y = doc.lastAutoTable.finalY + 20;
    }

    addPageFooter();

    // ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ
    //  PAGE 3 ΓÇö STRENGTHS & AREAS FOR GROWTH
    // ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ
    doc.addPage();
    addPageHeader();
    y = 65;

    txt('STRENGTHS & AREAS FOR GROWTH', mg, y, 14, DARK, 'bold');
    txt('Detailed evaluation feedback', mg, y + 16, 8.5, SLATE, 'normal');
    doc.setDrawColor(...BORDER);
    doc.line(mg, y + 22, pw - mg, y + 22);
    y += 34;

    // ΓöÇΓöÇ Strengths ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
    if (strengths.length > 0) {
      sectionHeader('Key Strengths', GREEN);
      strengths.forEach((item, i) => {
        const isObj = typeof item === 'object' && item !== null;
        const title = isObj ? (item.title || `Strength ${i + 1}`) : `Strength ${i + 1}`;
        const desc  = isObj ? (item.description || '') : String(item);
        const scoreVal = isObj ? item.score : null;

        const descLines = doc.splitTextToSize(desc, contentW - 28);
        const blockH = 22 + descLines.length * 11 + 8;
        checkPage(blockH + 8);

        roundRect(mg, y, contentW, blockH, 6, [240, 253, 244], [187, 247, 208]);
        fillRect(mg, y, 4, blockH, GREEN);

        txt(`${i + 1}. ${title}`, mg + 12, y + 14, 9.5, [22, 163, 74], 'bold');
        if (scoreVal !== null && scoreVal !== undefined) {
          roundRect(pw - mg - 52, y + 5, 44, 16, 4, GREEN);
          txt(`${scoreVal}/100`, pw - mg - 30, y + 16, 8, WHITE, 'bold', 'center');
        }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(...DARK);
        doc.text(descLines, mg + 12, y + 27);
        y += blockH + 10;
      });
    } else {
      checkPage(50);
      roundRect(mg, y, contentW, 40, 6, LIGHT, BORDER);
      txt('No specific strengths recorded for this session.', pw / 2, y + 24, 9, SLATE, 'normal', 'center');
      y += 54;
    }

    y += 6;

    // ΓöÇΓöÇ Areas for Growth ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
    if (weaknesses.length > 0) {
      sectionHeader('Areas for Growth', AMBER);
      weaknesses.forEach((item, i) => {
        const isObj = typeof item === 'object' && item !== null;
        const title = isObj ? (item.title || `Area ${i + 1}`) : `Area ${i + 1}`;
        const desc  = isObj ? (item.description || '') : String(item);
        const scoreVal = isObj ? item.score : null;

        const descLines = doc.splitTextToSize(desc, contentW - 28);
        const blockH = 22 + descLines.length * 11 + 8;
        checkPage(blockH + 8);

        roundRect(mg, y, contentW, blockH, 6, [255, 251, 235], [253, 230, 138]);
        fillRect(mg, y, 4, blockH, AMBER);

        txt(`${i + 1}. ${title}`, mg + 12, y + 14, 9.5, [180, 83, 9], 'bold');
        if (scoreVal !== null && scoreVal !== undefined) {
          roundRect(pw - mg - 52, y + 5, 44, 16, 4, AMBER);
          txt(`${scoreVal}/100`, pw - mg - 30, y + 16, 8, WHITE, 'bold', 'center');
        }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(...DARK);
        doc.text(descLines, mg + 12, y + 27);
        y += blockH + 10;
      });
    }

    addPageFooter();

    // ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ
    //  PAGE(S) ΓÇö DETAILED Q&A ANALYSIS WITH AI RECOMMENDATIONS
    // ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ
    if (qaList.length > 0) {
      doc.addPage();
      addPageHeader();
      y = 65;

      txt('DETAILED Q&A ANALYSIS', mg, y, 14, DARK, 'bold');
      txt('Question-by-question feedback with AI recommendations', mg, y + 16, 8.5, SLATE, 'normal');
      doc.setDrawColor(...BORDER);
      doc.line(mg, y + 22, pw - mg, y + 22);
      y += 34;

      qaList.forEach((qa, i) => {
        const sc     = qa.score !== undefined ? qa.score : null;
        const scCol  = sc !== null ? (sc >= 80 ? GREEN : sc >= 60 ? AMBER : RED) : SLATE;
        const qText  = String(qa.question || '');
        const aText  = String(qa.answer   || '');
        const fbText = String(qa.feedback || '');
        const idealText  = String(qa.ideal_answer || qa.recommended_answer || '');
        const improveTxt = String(qa.improvement  || qa.suggestion || '');

        const qLines  = doc.splitTextToSize(qText,  contentW - 24);
        const aLines  = aText ? doc.splitTextToSize(aText,  contentW - 24) : [];
        const fbLines = fbText ? doc.splitTextToSize(fbText, contentW - 24) : [];
        const idLines = idealText ? doc.splitTextToSize(idealText,  contentW - 24) : [];
        const imLines = improveTxt ? doc.splitTextToSize(improveTxt, contentW - 24) : [];

        const qH  = 12 + qLines.length * 11 + 6;
        const aH  = aText ? 12 + aLines.length * 11 + 6 : 0;
        const fbH = fbText ? 12 + fbLines.length * 11 + 6 : 0;
        const idH = idealText ? 12 + idLines.length * 11 + 6 : 0;
        const imH = improveTxt ? 12 + imLines.length * 11 + 6 : 0;
        const cardTotalH = 24 + qH + aH + fbH + idH + imH;

        checkPage(cardTotalH + 12);

        const cardStartY = y;

        // ΓöÇΓöÇ Card header bar ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
        fillRect(mg, y, contentW, 24, scCol);
        // Q badge (white pill)
        roundRect(mg + 6, y + 5, 28, 14, 4, WHITE);
        txt(`Q${i + 1}`, mg + 20, y + 15, 8.5, scCol, 'bold', 'center');
        // Status label
        if (sc !== null) {
          const statusLbl = sc >= 80 ? 'EXCELLENT' : sc >= 60 ? 'SATISFACTORY' : 'NEEDS WORK';
          txt(statusLbl, pw - mg - 110, y + 15, 7, WHITE, 'bold', 'right');
          // Score pill (white pill)
          roundRect(pw - mg - 52, y + 5, 44, 14, 4, WHITE);
          txt(`${sc}/100`, pw - mg - 30, y + 15, 8.5, scCol, 'bold', 'center');
        }
        y += 24;

        // ── Question ────────────────────────────────────────────────
        fillRect(mg, y, contentW, qH, [248, 250, 252]);
        txt('QUESTION', mg + 8, y + 9, 7, BLUE, 'bold');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(15, 23, 42);
        doc.text(qLines, mg + 8, y + 22);
        y += qH;

        // ΓöÇΓöÇ Student's Answer ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
        if (aText) {
          fillRect(mg, y, contentW, aH, [239, 246, 255]);
          txt('STUDENT ANSWER', mg + 8, y + 9, 7, BLUE, 'bold');
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(8);
          doc.setTextColor(51, 65, 85);
          doc.text(aLines, mg + 8, y + 22);
          y += aH;
        }

        // ΓöÇΓöÇ AI Feedback ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
        if (fbText) {
          fillRect(mg, y, contentW, fbH, [245, 243, 255]);
          txt('AI FEEDBACK', mg + 8, y + 9, 7, PURPLE, 'bold');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(15, 23, 42);
          doc.text(fbLines, mg + 8, y + 22);
          y += fbH;
        }

        // ── Correct / Model Answer (What you should have answered) ──
        if (idealText) {
          fillRect(mg, y, contentW, idH, [240, 253, 244]);
          txt('CORRECT / MODEL ANSWER (WHAT YOU SHOULD HAVE ANSWERED)', mg + 8, y + 9, 7, GREEN, 'bold');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(22, 101, 52);
          doc.text(idLines, mg + 8, y + 22);
          y += idH;
        }

        // ΓöÇΓöÇ How to Improve ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
        if (improveTxt) {
          fillRect(mg, y, contentW, imH, [254, 243, 199]);
          txt('HOW TO IMPROVE', mg + 8, y + 9, 7, AMBER, 'bold');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(15, 23, 42);
          doc.text(imLines, mg + 8, y + 22);
          y += imH;
        }

        // Card outer border
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.5);
        doc.rect(mg, cardStartY, contentW, cardTotalH);
        y += 16;
      });

      addPageFooter();
    }

    // ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ
    //  PAGE ΓÇö STUDENT IMPROVEMENT PLAN
    // ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ
    const hasImprovements = weaknesses.length > 0 || actionPlan.length > 0 || studyRecs.length > 0 || pathways.length > 0 || knowledgeGaps.length > 0;
    if (hasImprovements) {
      doc.addPage();
      addPageHeader();
      y = 75;

      txt('PERSONALIZED IMPROVEMENT PLAN', mg, y, 14, DARK, 'bold');
      txt('AI-generated action items for student growth', mg, y + 16, 8.5, SLATE, 'normal');
      doc.setDrawColor(...BORDER);
      doc.line(mg, y + 22, pw - mg, y + 22);
      y += 36;

      // ΓöÇΓöÇ Priority Focus Areas (from weaknesses) ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
      if (weaknesses.length > 0) {
        sectionHeader('Priority Focus Areas', RED);
        weaknesses.forEach((item, i) => {
          const isObj = typeof item === 'object' && item !== null;
          const title = isObj ? (item.title || `Area ${i + 1}`) : `Area ${i + 1}`;
          const desc  = isObj ? (item.description || '') : String(item);
          const lines = doc.splitTextToSize(desc, contentW - 44);
          const bH = lines.length * 11 + 28;
          checkPage(bH + 8);

          roundRect(mg, y, contentW, bH, 6, LIGHT, BORDER);
          // Step number circle
          roundRect(mg + 8, y + (bH / 2) - 12, 24, 24, 12, RED);
          txt(`${i + 1}`, mg + 20, y + bH / 2 + 4, 10, WHITE, 'bold', 'center');

          txt(title, mg + 40, y + 17, 9.5, DARK, 'bold');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8.5);
          doc.setTextColor(...SLATE);
          doc.text(lines, mg + 40, y + 30);
          y += bH + 8;
        });
        y += 6;
      }

      // ΓöÇΓöÇ Action Plan ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
      if (actionPlan.length > 0) {
        sectionHeader('Recommended Action Plan', PURPLE);
        actionPlan.forEach((item, i) => {
          const stepText = item.step || item.title || `Step ${i + 1}`;
          const descText = item.description || item.detail || '';
          const descLines = doc.splitTextToSize(descText, contentW - 50);
          const bH = 24 + (descLines.length ? descLines.length * 11 + 8 : 0);
          checkPage(bH + 8);

          roundRect(mg, y, contentW, bH, 6, LIGHT, BORDER);
          roundRect(mg + 8, y + 8, 24, 24, 12, PURPLE);
          txt(`${i + 1}`, mg + 20, y + 22, 10, WHITE, 'bold', 'center');

          txt(stepText, mg + 40, y + 17, 9.5, DARK, 'bold');
          if (descText) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8.5);
            doc.setTextColor(...SLATE);
            doc.text(descLines, mg + 40, y + 30);
          }
          y += bH + 8;
        });
        y += 6;
      }

      // ΓöÇΓöÇ Study Recommendations ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
      if (studyRecs.length > 0) {
        sectionHeader('Study Recommendations', BLUE);
        studyRecs.forEach((item, i) => {
          const resourceText = item.resource || `Resource ${i + 1}`;
          const reasonText   = item.reason   || item.description || '';
          const rLines = doc.splitTextToSize(reasonText, contentW - 50);
          const bH = 24 + (rLines.length ? rLines.length * 11 + 8 : 0);
          checkPage(bH + 8);

          roundRect(mg, y, contentW, bH, 6, [239, 246, 255], [191, 219, 254]);
          fillRect(mg, y, 4, bH, BLUE);
          txt(`Resource: ${resourceText}`, mg + 14, y + 17, 9.5, [30, 64, 175], 'bold');
          if (reasonText) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8.5);
            doc.setTextColor(...DARK);
            doc.text(rLines, mg + 14, y + 30);
          }
          y += bH + 8;
        });
        y += 6;
      }

      // ΓöÇΓöÇ Knowledge Gaps ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
      if (knowledgeGaps.length > 0) {
        sectionHeader('Knowledge Gaps to Address', AMBER);
        knowledgeGaps.forEach((item, i) => {
          const concept = item.concept || `Gap ${i + 1}`;
          const descText = item.description || item.explanation || '';
          const gLines = doc.splitTextToSize(descText, contentW - 24);
          const bH = 18 + (gLines.length ? gLines.length * 11 + 8 : 0);
          checkPage(bH + 8);

          roundRect(mg, y, contentW, bH, 6, [255, 251, 235], [253, 230, 138]);
          fillRect(mg, y, 4, bH, AMBER);
          txt(concept, mg + 14, y + 14, 9.5, [180, 83, 9], 'bold');
          if (descText) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8.5);
            doc.setTextColor(...DARK);
            doc.text(gLines, mg + 14, y + 26);
          }
          y += bH + 8;
        });
        y += 6;
      }

      // ΓöÇΓöÇ Career Pathways ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
      if (pathways.length > 0) {
        sectionHeader('Recommended Career Pathways', [14, 165, 233]);
        const colW = (contentW - 12) / 2;
        pathways.forEach((item, i) => {
          if (i % 2 === 0) checkPage(90);
          const px = i % 2 === 0 ? mg : mg + colW + 12;
          const pathway = item.title || item.pathway || `Pathway ${i + 1}`;
          const reason  = item.description || item.reason || '';
          const rLines = doc.splitTextToSize(reason, colW - 20);
          const bH = 20 + rLines.length * 11 + 14;

          roundRect(px, y, colW, bH, 8, [240, 249, 255], [186, 230, 253]);
          fillRect(px, y, 4, bH, [14, 165, 233]);
          txt(pathway, px + 12, y + 14, 9, [7, 89, 133], 'bold');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(...DARK);
          doc.text(rLines, px + 12, y + 26);

          if (i % 2 === 1 || i === pathways.length - 1) {
            y += bH + 10;
          }
        });
      }

      addPageFooter();
    }

    // ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ
    //  PAGE ΓÇö SESSION TRANSCRIPT
    // ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ
    if (transcript) {
      doc.addPage();
      addPageHeader();
      y = 75;

      txt('SESSION TRANSCRIPT', mg, y, 14, DARK, 'bold');
      txt('Full conversation log from the AI session', mg, y + 16, 8.5, SLATE, 'normal');
      doc.setDrawColor(...BORDER);
      doc.line(mg, y + 22, pw - mg, y + 22);
      y += 36;

      const lines = transcript.split('\n').filter(l => l.trim());
      lines.forEach((line, i) => {
        const isAI   = line.startsWith('[AI') || line.toLowerCase().startsWith('ai:') || line.startsWith('[Interviewer') || line.startsWith('[Tutor') || line.startsWith('[Counsellor');
        const clean  = line.replace(/^\[.*?\]\s*/, '').trim();
        if (!clean) return;

        const speaker = isAI ? 'AI' : 'Student';
        const bgColor = isAI ? [239, 246, 255] : [240, 253, 244];
        const borderCol = isAI ? BLUE : GREEN;
        const linesTxt = doc.splitTextToSize(clean, contentW - 85);
        const bH = linesTxt.length * 11 + 22;
        checkPage(bH + 8);

        roundRect(mg, y, contentW, bH, 6, bgColor, borderCol);
        fillRect(mg, y, 4, bH, borderCol);
        roundRect(mg + 10, y + 6, 38, 12, 4, borderCol);
        txt(speaker, mg + 29, y + 15, 7, WHITE, 'bold', 'center');

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(...DARK);
        doc.text(linesTxt, mg + 56, y + 14);
        y += bH + 7;
      });

      addPageFooter();
    }

    // ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ
    //  RETROACTIVELY ADD PAGE NUMBERS TO FOOTER
    // ΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉΓòÉ
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      // Overwrite just the page number portion
      doc.setFillColor(245, 248, 252);
      doc.rect(pw - mg - 60, ph - 28, 60, 20, 'F');
      txt(`Page ${i} / ${totalPages}`, pw - mg, ph - 12, 7.5, SLATE, 'normal', 'right');
    }

    const cleanDate = createdDate.toISOString().slice(0, 10);
    const pdfFilename = itemMode === 'interviewer' ? `Interviewer_Performance_Report_${cleanDate}.pdf`
                      : itemMode === 'counsellor'  ? `AI_Counsellor_Report_${cleanDate}.pdf`
                      : `AI_Tutor_Report_${cleanDate}.pdf`;
    doc.save(pdfFilename);
    setDownloadState(null);
    } catch (err) {
      console.error('PDF generation failed:', err);
      setDownloadState('error');
      setTimeout(() => setDownloadState(null), 5000);
    }
  };

  const currentRawMode = String(reportData?.mode || mode || '').toLowerCase();
  const isCurrentCounsellor = currentRawMode.includes('counsel') || reportData?.report?.career_clarity !== undefined || Array.isArray(reportData?.report?.pathways) || Array.isArray(reportData?.report?.action_plan);
  const isCurrentTutor = currentRawMode.includes('tutor') || reportData?.report?.topic_mastery !== undefined || Array.isArray(reportData?.report?.key_concepts_learned);
  const effectiveMode = isCurrentCounsellor ? 'counsellor' : (isCurrentTutor ? 'tutor' : 'interviewer');

  const modeTitle = effectiveMode === 'interviewer' ? 'Interviewer Performance Report' : effectiveMode === 'counsellor' ? 'AI Counsellor Guidance Report' : 'AI Tutor Academic Report';

  const renderHistoryModal = () => {
    if (!showHistoryModal) return null;

    return createPortal(
      <div className="perf-history-overlay" onClick={handleCloseHistory} style={{ zIndex: 10000 }}>
        <div className="perf-history-modal" onClick={(e) => e.stopPropagation()}>
          <div className="perf-history-header">
            <div className="perf-history-title-group">
              <History size={20} className="perf-history-icon" />
              <div>
                <h3>Previous Performance Reports</h3>
                <p className="perf-history-subtitle">View all previously saved AI evaluation reports</p>
              </div>
            </div>
            <button className="perf-history-close-btn" onClick={handleCloseHistory} title="Close">
              <X size={18} />
            </button>
          </div>

          <div className="perf-history-body">
            {loadingHistory ? (
              <div className="perf-history-loading">
                <div className="perf-spinner" />
                <p>Loading previous reports from database...</p>
              </div>
            ) : historyError ? (
              <div className="perf-history-error">
                <p>{historyError}</p>
                <button className="perf-refresh-btn" onClick={fetchHistory}>
                  <RefreshCw size={14} /> Retry
                </button>
              </div>
            ) : historyReports.length === 0 ? (
              <div className="perf-history-empty">
                <FileText size={40} style={{ opacity: 0.5, marginBottom: '12px' }} />
                <h4>No Previous Reports Found</h4>
                <p>Complete an AI Interview session to save your evaluation reports here.</p>
              </div>
            ) : (
              <div className="perf-history-list">
                {historyReports.map((item) => {
                  const repData = item.report || {};
                  const strengthsCount = (repData.strengths || []).length;
                  const weaknessesCount = (repData.weaknesses || []).length;
                  const dateStr = item.createdAt ? new Date(item.createdAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  }) : 'Unknown Date';
                  const isCurrent = reportData && reportData.sessionId === item.sessionId;

                  return (
                    <div 
                      key={item.id || item.sessionId} 
                      className={`perf-history-item ${isCurrent ? 'active' : ''}`}
                      onClick={() => handleSelectHistoryReport(item)}
                    >
                      <div className="perf-history-item-header">
                        <div className="perf-history-item-meta">
                          <span className="perf-history-badge">
                            {item.mode ? item.mode.toUpperCase() : 'INTERVIEWER'}
                          </span>
                        </div>
                        <div className="perf-history-item-date">
                          <Calendar size={13} />
                          <span>{dateStr}</span>
                        </div>
                      </div>

                      <div className="perf-history-item-summary">
                        <div className="perf-history-stat-tag strength">
                          <CheckCircle2 size={13} /> {strengthsCount} Strengths
                        </div>
                        <div className="perf-history-stat-tag weakness">
                          <AlertTriangle size={13} /> {weaknessesCount} Areas for Growth
                        </div>
                      </div>

                      <div className="perf-history-item-footer" style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <button 
                          className="perf-history-view-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectHistoryReport(item);
                          }}
                        >
                          {isCurrent ? 'Viewing Report' : 'View Report'}
                        </button>
                        <button
                          className="perf-history-download-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadReportPDF(item);
                          }}
                          disabled={downloadState === 'downloading'}
                          title={downloadState === 'error' ? 'Download failed. Click to retry.' : 'Download PDF Report'}
                          style={downloadState === 'error' ? { color: '#dc2626', borderColor: '#dc2626' } : {}}
                        >
                          <Download size={14} />
                          {downloadState === 'downloading' ? 'Generating...' : downloadState === 'error' ? 'Failed — Retry' : 'Download PDF'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  if (loading) {
    return (
      <>
        {createPortal(
          <div className="perf-history-overlay" style={{ zIndex: 9998 }}>
            <div className="perf-report-card loading" style={{ maxWidth: '600px', margin: '0 auto', marginTop: 0 }}>
              <div className="perf-spinner" />
              <p>Analyzing transcript and generating AI performance feedback...</p>
            </div>
          </div>,
          document.body
        )}
        {renderHistoryModal()}
      </>
    );
  }

  if (!reportData || !reportData.report) {
    return <>{renderHistoryModal()}</>;
  }

  const { strengths = [], weaknesses = [], layers = {}, fit_bucket = "N/A", killer_question_failed = false, pathways = [], action_plan = [], key_concepts_learned = [], knowledge_gaps = [], study_recommendations = [], proctoring = null } = reportData.report;
  // Present only when the session ended automatically (e.g. student inactivity).
  const termination = reportData.report.termination || null;

  // Use composite_score as the main score. If not available, use overall_score or fallback to 0.
  const getRawScore = () => {
    const rawReport = reportData.report;
    if (rawReport.topic_mastery !== undefined && rawReport.topic_mastery !== null) return rawReport.topic_mastery;
    if (rawReport.career_clarity !== undefined && rawReport.career_clarity !== null) return rawReport.career_clarity;
    if (rawReport.composite_score !== undefined && rawReport.composite_score !== null) return rawReport.composite_score;
    if (rawReport.overall_score !== undefined && rawReport.overall_score !== null) return rawReport.overall_score;
    return 0;
  };
  let calculatedScore = getRawScore();

  // Get score color
  const getScoreColor = (score) => {
    if (score >= 80) return '#10b981'; // green
    if (score >= 60) return '#f59e0b'; // orange
    return '#ef4444'; // red
  };
  const scoreColor = getScoreColor(calculatedScore);

  const studentName = reportData?.studentName || userProfile?.full_name || userProfile?.name || 'Candidate';
  const institutionName = reportData?.institutionName || userProfile?.institution_name || userProfile?.institution || '';
  const usn = reportData?.usn || userProfile?.usn || '';
  const startTimeVal = reportData?.startTime || reportData?.createdAt;
  const endTimeVal = reportData?.endTime || reportData?.completedAt;

  const formatTimeStr = (dateVal) => {
    if (!dateVal) return '';
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return String(dateVal);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };
  const formatDateStr = (dateVal) => {
    if (!dateVal) return '';
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return String(dateVal);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  return (
    <>
      {createPortal(
        <div className="perf-history-overlay" style={{ zIndex: 9998, padding: 0, background: '#ffffff' }}>
          <div className="perf-report-card" style={{ display: 'flex', flexDirection: 'row', width: '100%', height: '100%', margin: 0, borderRadius: 0, background: '#ffffff', overflow: 'hidden', border: 'none', boxShadow: 'none', boxSizing: 'border-box' }}>
            
            {/* LEFT PANEL - Sticky Score & Info */}
            <div style={{ width: '320px', minWidth: '320px', maxWidth: '320px', flexShrink: 0, background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)', borderRight: '1px solid rgba(0,0,0,0.05)', padding: '32px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', overflowY: 'auto', boxSizing: 'border-box' }}>
              
              {/* Decorative Glow */}
              <div style={{ position: 'absolute', top: '-100px', left: '-100px', width: '300px', height: '300px', background: `radial-gradient(circle, ${scoreColor}15 0%, transparent 70%)`, borderRadius: '50%', pointerEvents: 'none' }}></div>
              
              {onCloseReport && (
                <button
                  onClick={onCloseReport}
                  style={{
                    width: '100%',
                    background: '#0f172a',
                    border: 'none',
                    color: '#ffffff',
                    padding: '11px 16px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontSize: '13px',
                    fontWeight: '700',
                    boxShadow: '0 4px 12px rgba(15,23,42,0.18)',
                    marginBottom: '24px',
                    zIndex: 1,
                    boxSizing: 'border-box'
                  }}
                >
                  <ArrowLeft size={16} /> {backButtonText || 'Back to AI Interviewer Page'}
                </button>
              )}

              {termination && (
                <div style={{ width: '100%', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '10px 12px', marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'flex-start', zIndex: 1, boxSizing: 'border-box' }}>
                  <AlertTriangle size={16} color="#b91c1c" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div style={{ fontSize: '11.5px', lineHeight: '1.5', color: '#991b1b', fontWeight: '600' }}>
                    Terminated automatically — {termination.reason_label || 'Student inactivity'}
                    {termination.inactivity_display ? ` (no response for ${termination.inactivity_display})` : ''}.
                    {' '}Answered {termination.questions_answered ?? 0} of {termination.questions_asked ?? 0} questions
                    {termination.questions_unanswered ? `, ${termination.questions_unanswered} unanswered` : ''}.
                  </div>
                </div>
              )}

              <div style={{ background: '#e0f2fe', border: '1px solid #bae6fd', padding: '6px 14px', borderRadius: '20px', fontSize: '11px', color: '#0284c7', fontWeight: '700', letterSpacing: '1px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '28px', zIndex: 1 }}>
                <Sparkles size={14} /> {effectiveMode === 'interviewer' ? 'AI EVALUATION' : 'AI GUIDANCE'}
              </div>
              
              {effectiveMode === 'interviewer' ? (
                <div style={{ position: 'relative', zIndex: 1, marginBottom: '24px' }}>
                  <div style={{ width: '160px', height: '160px', borderRadius: '50%', background: `conic-gradient(${scoreColor} ${calculatedScore}%, #e2e8f0 0)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', boxShadow: `0 4px 20px ${scoreColor}20`, transition: 'all 0.5s ease', boxSizing: 'border-box' }}>
                    <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: '#ffffff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', boxSizing: 'border-box' }}>
                      <span style={{ fontSize: '48px', fontWeight: '800', color: '#0f172a', lineHeight: '1' }}>{calculatedScore}</span>
                      <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', letterSpacing: '1px', marginTop: '4px' }}>SCORE</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ position: 'relative', zIndex: 1, marginBottom: '24px' }}>
                  <div style={{ width: '140px', height: '140px', borderRadius: '50%', background: 'linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '3px solid #bae6fd', boxShadow: '0 8px 25px rgba(2, 132, 199, 0.15)', boxSizing: 'border-box' }}>
                    <Compass size={48} color="#0284c7" />
                    <span style={{ fontSize: '11px', color: '#0369a1', fontWeight: '800', letterSpacing: '1px', marginTop: '8px' }}>GUIDANCE</span>
                  </div>
                </div>
              )}

              <h2 style={{ fontSize: '19px', margin: '0 0 8px 0', fontWeight: '700', color: '#0f172a', textAlign: 'center', zIndex: 1, lineHeight: '1.3' }}>{modeTitle}</h2>
              {effectiveMode === 'interviewer' && (
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px', zIndex: 1, width: '100%', alignItems: 'center', boxSizing: 'border-box' }}>
                  <div style={{ 
                    background: getFitBucketStyle(fit_bucket).bg, 
                    color: getFitBucketStyle(fit_bucket).text, 
                    padding: '6px 14px', 
                    borderRadius: '12px', 
                    fontSize: '12.5px', 
                    fontWeight: '700', 
                    border: `1px solid ${getFitBucketStyle(fit_bucket).border}`,
                    textAlign: 'center',
                    boxSizing: 'border-box',
                    maxWidth: '100%'
                  }}>
                    FIT BUCKET: {normalizeFitBucket(fit_bucket).toUpperCase()}
                  </div>
                  {killer_question_failed && (
                    <div style={{ color: '#dc2626', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', textAlign: 'center' }}>
                      <AlertTriangle size={14} /> Failed Killer Question
                    </div>
                  )}
                  <div style={{
                    marginTop: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: (proctoring?.integrity_score ?? 100) >= 80 ? '#dcfce7' : (proctoring?.integrity_score ?? 100) >= 60 ? '#fef3c7' : '#fee2e2',
                    border: `1px solid ${(proctoring?.integrity_score ?? 100) >= 80 ? '#bbf7d0' : (proctoring?.integrity_score ?? 100) >= 60 ? '#fde68a' : '#fecaca'}`,
                    padding: '5px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: (proctoring?.integrity_score ?? 100) >= 80 ? '#15803d' : (proctoring?.integrity_score ?? 100) >= 60 ? '#b45309' : '#b91c1c',
                    boxSizing: 'border-box'
                  }}>
                    <ShieldCheck size={14} />
                    <span>Integrity: <strong>{proctoring?.integrity_score ?? 100}%</strong></span>
                  </div>
                </div>
              )}

              {/* Student Metadata Card */}
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '14px', width: '100%', marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '12px', textAlign: 'left', zIndex: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0f172a', fontWeight: '700' }}>
                  <User size={15} color="#3b82f6" style={{ flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{studentName}</span>
                </div>
                {usn && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
                    <Hash size={14} color="#94a3b8" style={{ flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>USN: <strong style={{ color: '#334155' }}>{usn}</strong></span>
                  </div>
                )}
                {institutionName && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
                    <Building2 size={14} color="#94a3b8" style={{ flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{institutionName}</span>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
                  <Calendar size={14} color="#94a3b8" style={{ flexShrink: 0 }} />
                  <span>{formatDateStr(reportData.createdAt || reportData.startTime || new Date())}</span>
                </div>
                {(startTimeVal || endTimeVal) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
                    <Clock size={14} color="#94a3b8" style={{ flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {formatTimeStr(startTimeVal)}
                      {endTimeVal && startTimeVal !== endTimeVal ? ` - ${formatTimeStr(endTimeVal)}` : ''}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', zIndex: 1, marginTop: '28px', boxSizing: 'border-box' }}>
                <button
                  onClick={() => downloadReportPDF(reportData)}
                  disabled={downloadState === 'downloading'}
                  style={{
                    background: downloadState === 'error' ? '#dc2626' : '#3b82f6',
                    border: 'none', color: '#ffffff', padding: '12px', borderRadius: '12px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    cursor: downloadState === 'downloading' ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s', fontSize: '13px', fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(59,130,246,0.25)', boxSizing: 'border-box',
                    opacity: downloadState === 'downloading' ? 0.75 : 1
                  }}
                >
                  <Download size={16} />
                  {downloadState === 'downloading' ? 'Generating PDF...' : downloadState === 'error' ? 'Download Failed — Retry' : 'Download PDF Report'}
                </button>
                <button onClick={handleOpenHistory} style={{ background: '#ffffff', border: '1px solid #e2e8f0', color: '#334155', padding: '12px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s', fontSize: '13px', fontWeight: '600', boxShadow: '0 2px 5px rgba(0,0,0,0.02)', boxSizing: 'border-box' }}>
                  <History size={16} /> Previous Reports
                </button>
                {onRefresh && (
                  <button onClick={onRefresh} style={{ background: '#ffffff', border: '1px solid #e2e8f0', color: '#334155', padding: '12px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s', fontSize: '13px', fontWeight: '600', boxShadow: '0 2px 5px rgba(0,0,0,0.02)', boxSizing: 'border-box' }}>
                    <RefreshCw size={16} /> Refresh
                  </button>
                )}
              </div>
            </div>

            {/* RIGHT PANEL - Scrollable Insight Feed */}
            <div style={{ flex: 1, padding: '48px 56px', overflowY: 'auto', background: '#f8fafc', position: 'relative' }}>
              <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
                <div>
                  <h3 style={{ color: '#0f172a', fontSize: '28px', fontWeight: '800', margin: '0 0 8px 0', letterSpacing: '-0.5px' }}>
                    Detailed Analysis
                  </h3>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '15px' }}>Breakdown of your AI evaluation</p>
                </div>
                {onCloseReport && (
                  <button
                    onClick={onCloseReport}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #cbd5e1',
                      color: '#0f172a',
                      padding: '10px 18px',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '700',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                      transition: 'all 0.2s'
                    }}
                  >
                    <ArrowLeft size={16} /> {backButtonText || 'Back to AI Interviewer Page'}
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>
                
                {/* PROCTORING INTEGRITY AUDIT SECTION (FOR INTERVIEWER MODE) */}
                {effectiveMode === 'interviewer' && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)', color: '#ffffff', padding: '10px', borderRadius: '12px', boxShadow: '0 8px 16px rgba(99,102,241,0.2)' }}>
                          <ShieldCheck size={20} />
                        </div>
                        <div>
                          <h4 style={{ margin: 0, fontSize: '20px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.3px' }}>Assessment Integrity & Proctoring</h4>
                          <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>Real-time automated environmental & behavioral compliance log</p>
                        </div>
                      </div>
                      <div style={{
                        background: (proctoring?.integrity_score ?? 100) >= 80 ? '#dcfce7' : (proctoring?.integrity_score ?? 100) >= 60 ? '#fef3c7' : '#fee2e2',
                        color: (proctoring?.integrity_score ?? 100) >= 80 ? '#15803d' : (proctoring?.integrity_score ?? 100) >= 60 ? '#b45309' : '#b91c1c',
                        padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '800', border: '1px solid rgba(0,0,0,0.06)'
                      }}>
                        {(proctoring?.integrity_score ?? 100) >= 80 ? 'HIGH INTEGRITY' : (proctoring?.integrity_score ?? 100) >= 60 ? 'WARNINGS FLAGGED' : 'INTEGRITY RISK'}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '16px' }}>
                      <div style={{ background: '#ffffff', padding: '18px', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>Integrity Score</div>
                        <div style={{ fontSize: '28px', fontWeight: '800', color: (proctoring?.integrity_score ?? 100) >= 80 ? '#10b981' : (proctoring?.integrity_score ?? 100) >= 60 ? '#f59e0b' : '#ef4444', margin: '4px 0' }}>
                          {proctoring?.integrity_score ?? 100}%
                        </div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>Overall adherence index</div>
                      </div>

                      <div style={{ background: '#ffffff', padding: '18px', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>Tab / Focus Switches</div>
                        <div style={{ fontSize: '28px', fontWeight: '800', color: (proctoring?.breakdown?.tabSwitches || 0) === 0 ? '#10b981' : '#ef4444', margin: '4px 0' }}>
                          {proctoring?.breakdown?.tabSwitches || 0}
                        </div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>Browser focus departures</div>
                      </div>

                      <div style={{ background: '#ffffff', padding: '18px', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>Camera Presence</div>
                        <div style={{ fontSize: '28px', fontWeight: '800', color: (proctoring?.breakdown?.faceAbsence || 0) === 0 ? '#10b981' : '#f59e0b', margin: '4px 0' }}>
                          {(proctoring?.breakdown?.faceAbsence || 0) === 0 ? 'Verified' : `${proctoring.breakdown.faceAbsence} Absences`}
                        </div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>Candidate in camera view</div>
                      </div>

                      <div style={{ background: '#ffffff', padding: '18px', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>Device & Phone Usage</div>
                        <div style={{ fontSize: '28px', fontWeight: '800', color: (proctoring?.breakdown?.phoneDetected || 0) === 0 ? '#10b981' : '#ef4444', margin: '4px 0' }}>
                          {(proctoring?.breakdown?.phoneDetected || 0) === 0 ? 'Clear' : `${proctoring.breakdown.phoneDetected} Detected`}
                        </div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>Mobile phone / second screen</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* LAYERS (Moved to top as a beautiful grid) */}
                {effectiveMode === 'interviewer' && layers && Object.keys(layers).length > 0 && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                      <div style={{ background: 'linear-gradient(135deg, #3b82f6, #0ea5e9)', color: '#ffffff', padding: '10px', borderRadius: '12px', boxShadow: '0 8px 16px rgba(59,130,246,0.2)' }}>
                        <Award size={20} />
                      </div>
                      <h4 style={{ margin: 0, fontSize: '20px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.3px' }}>Performance Layers</h4>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                      {Object.entries(layers).map(([layerName, score], idx) => (
                        <div key={idx} className="perf-layer-card" style={{ background: '#ffffff', padding: '20px', borderRadius: '20px', border: '1px solid rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.02)', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', cursor: 'default' }}>
                          <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {layerName.replace('_', ' ')}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                            <span style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', lineHeight: '1' }}>{score}</span>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>/100</span>
                          </div>
                          <div style={{ width: '100%', height: '6px', background: '#f1f5f9', borderRadius: '10px', overflow: 'hidden' }}>
                            <div style={{ width: `${score}%`, height: '100%', background: score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444', borderRadius: '10px', transition: 'width 1s ease-out' }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                  {/* STRENGTHS */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                      <div style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#ffffff', padding: '10px', borderRadius: '12px', boxShadow: '0 8px 16px rgba(16,185,129,0.2)' }}>
                        <CheckCircle2 size={20} />
                      </div>
                      <h4 style={{ margin: 0, fontSize: '20px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.3px' }}>Key Strengths</h4>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {strengths.length > 0 ? strengths.map((item, idx) => {
                        const isObj = typeof item === 'object' && item !== null;
                        const title = isObj ? item.title : null;
                        const score = isObj ? item.score : null;
                        const desc = isObj ? item.description : item;
                        return (
                          <div key={idx} style={{ background: '#ffffff', border: '1px solid rgba(16,185,129,0.1)', padding: '20px 24px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)', position: 'relative', overflow: 'hidden' }}>
                            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: '#10b981' }}></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: title ? '8px' : '0' }}>
                              {title && <span style={{ fontWeight: '800', color: '#0f172a', fontSize: '15px' }}>{title}</span>}
                              {effectiveMode !== 'counsellor' && score !== null && score !== undefined && (
                                <span style={{ background: '#dcfce7', color: '#16a34a', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: '700' }}>{score}/100</span>
                              )}
                            </div>
                            <p style={{ margin: 0, fontSize: '14.5px', color: '#475569', lineHeight: '1.6' }}>{desc}</p>
                          </div>
                        );
                      }) : (
                        <div style={{ background: '#ffffff', padding: '24px', borderRadius: '20px', textAlign: 'center', border: '1px dashed #cbd5e1' }}>
                          <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px', fontStyle: 'italic' }}>No specific strengths recorded.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* WEAKNESSES */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                      <div style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#ffffff', padding: '10px', borderRadius: '12px', boxShadow: '0 8px 16px rgba(245,158,11,0.2)' }}>
                        <AlertTriangle size={20} />
                      </div>
                      <h4 style={{ margin: 0, fontSize: '20px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.3px' }}>Areas for Growth</h4>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {weaknesses.length > 0 ? weaknesses.map((item, idx) => {
                        const isObj = typeof item === 'object' && item !== null;
                        const title = isObj ? item.title : null;
                        const score = isObj ? item.score : null;
                        const desc = isObj ? item.description : item;
                        return (
                          <div key={idx} style={{ background: '#ffffff', border: '1px solid rgba(245,158,11,0.1)', padding: '20px 24px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)', position: 'relative', overflow: 'hidden' }}>
                            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: '#f59e0b' }}></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: title ? '8px' : '0' }}>
                              {title && <span style={{ fontWeight: '800', color: '#0f172a', fontSize: '15px' }}>{title}</span>}
                              {effectiveMode !== 'counsellor' && score !== null && score !== undefined && (
                                <span style={{ background: '#ffedd5', color: '#ea580c', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: '700' }}>{score}/100</span>
                              )}
                            </div>
                            <p style={{ margin: 0, fontSize: '14.5px', color: '#475569', lineHeight: '1.6' }}>{desc}</p>
                          </div>
                        );
                      }) : (
                        <div style={{ background: '#ffffff', padding: '24px', borderRadius: '20px', textAlign: 'center', border: '1px dashed #cbd5e1' }}>
                          <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px', fontStyle: 'italic' }}>No major weaknesses identified.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* COUNSELLOR SPECIFIC: PATHWAYS & ACTION PLAN */}
                {effectiveMode === 'counsellor' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '48px', marginTop: '16px' }}>
                    
                    {/* RECOMMENDED PATHWAYS */}
                    {pathways && pathways.length > 0 && (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                          <div style={{ background: 'linear-gradient(135deg, #0ea5e9, #0284c7)', color: '#ffffff', padding: '10px', borderRadius: '12px', boxShadow: '0 8px 16px rgba(14,165,233,0.2)' }}>
                            <Compass size={20} />
                          </div>
                          <h4 style={{ margin: 0, fontSize: '20px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.3px' }}>Recommended Pathways</h4>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                          {pathways.map((item, idx) => (
                            <div key={idx} style={{ background: '#ffffff', padding: '24px', borderRadius: '20px', border: '1px solid rgba(14,165,233,0.1)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Target size={18} color="#0ea5e9" />
                                <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>{item.title || item.pathway}</h5>
                              </div>
                              <p style={{ margin: 0, fontSize: '14.5px', color: '#475569', lineHeight: '1.6' }}>{item.description || item.reason}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ACTION PLAN */}
                    {action_plan && action_plan.length > 0 && (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                          <div style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: '#ffffff', padding: '10px', borderRadius: '12px', boxShadow: '0 8px 16px rgba(139,92,246,0.2)' }}>
                            <ListChecks size={20} />
                          </div>
                          <h4 style={{ margin: 0, fontSize: '20px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.3px' }}>Action Plan</h4>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', background: '#ffffff', padding: '32px', borderRadius: '24px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 25px rgba(0,0,0,0.02)' }}>
                          {action_plan.map((item, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: '16px' }}>
                              <div style={{ minWidth: '28px', height: '28px', borderRadius: '50%', background: '#e0e7ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '800' }}>
                                {idx + 1}
                              </div>
                              <div>
                                <h5 style={{ margin: '0 0 4px 0', fontSize: '15.5px', fontWeight: '700', color: '#0f172a' }}>{item.step}</h5>
                                <p style={{ margin: 0, fontSize: '14.5px', color: '#64748b', lineHeight: '1.6' }}>{item.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* TUTOR SPECIFIC: ACADEMIC FEEDBACK */}
                {effectiveMode === 'tutor' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '48px', marginTop: '16px' }}>
                    
                    {/* KEY CONCEPTS LEARNED */}
                    {key_concepts_learned && key_concepts_learned.length > 0 && (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                          <div style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#ffffff', padding: '10px', borderRadius: '12px', boxShadow: '0 8px 16px rgba(16,185,129,0.2)' }}>
                            <BookOpen size={20} />
                          </div>
                          <h4 style={{ margin: 0, fontSize: '20px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.3px' }}>Key Concepts Learned</h4>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {key_concepts_learned.map((item, idx) => (
                            <div key={idx} style={{ background: '#ffffff', border: '1px solid rgba(16,185,129,0.1)', padding: '20px 24px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)', position: 'relative', overflow: 'hidden' }}>
                              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: '#10b981' }}></div>
                              <span style={{ fontWeight: '800', color: '#0f172a', fontSize: '15px', display: 'block', marginBottom: '8px' }}>{item.concept}</span>
                              <p style={{ margin: 0, fontSize: '14.5px', color: '#475569', lineHeight: '1.6' }}>{item.explanation}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* KNOWLEDGE GAPS */}
                    {knowledge_gaps && knowledge_gaps.length > 0 && (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                          <div style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#ffffff', padding: '10px', borderRadius: '12px', boxShadow: '0 8px 16px rgba(245,158,11,0.2)' }}>
                            <BrainCircuit size={20} />
                          </div>
                          <h4 style={{ margin: 0, fontSize: '20px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.3px' }}>Knowledge Gaps</h4>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {knowledge_gaps.map((item, idx) => (
                            <div key={idx} style={{ background: '#ffffff', border: '1px solid rgba(245,158,11,0.1)', padding: '20px 24px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)', position: 'relative', overflow: 'hidden' }}>
                              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: '#f59e0b' }}></div>
                              <span style={{ fontWeight: '800', color: '#0f172a', fontSize: '15px', display: 'block', marginBottom: '8px' }}>{item.concept}</span>
                              <p style={{ margin: 0, fontSize: '14.5px', color: '#475569', lineHeight: '1.6' }}>{item.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* STUDY RECOMMENDATIONS */}
                    {study_recommendations && study_recommendations.length > 0 && (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                          <div style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: '#ffffff', padding: '10px', borderRadius: '12px', boxShadow: '0 8px 16px rgba(139,92,246,0.2)' }}>
                            <Library size={20} />
                          </div>
                          <h4 style={{ margin: 0, fontSize: '20px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.3px' }}>Study Recommendations</h4>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', background: '#ffffff', padding: '32px', borderRadius: '24px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 25px rgba(0,0,0,0.02)' }}>
                          {study_recommendations.map((item, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: '16px' }}>
                              <div style={{ minWidth: '28px', height: '28px', borderRadius: '50%', background: '#e0e7ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '800' }}>
                                {idx + 1}
                              </div>
                              <div>
                                <h5 style={{ margin: '0 0 4px 0', fontSize: '15.5px', fontWeight: '700', color: '#0f172a' }}>{item.resource}</h5>
                                <p style={{ margin: 0, fontSize: '14.5px', color: '#64748b', lineHeight: '1.6' }}>{item.reason}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* DETAILED Q/A BREAKDOWN */}
                {reportData.report.qa_breakdown && reportData.report.qa_breakdown.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                      <div style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: '#ffffff', padding: '10px', borderRadius: '12px', boxShadow: '0 8px 16px rgba(139,92,246,0.2)' }}>
                        <MessageSquare size={20} />
                      </div>
                      <h4 style={{ margin: 0, fontSize: '20px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.3px' }}>Detailed Q/A Analysis</h4>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {reportData.report.qa_breakdown.map((qa, idx) => (
                        <div key={idx} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '24px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ display: 'flex', gap: '16px' }}>
                              <div style={{ minWidth: '40px', height: '40px', borderRadius: '50%', background: '#f1f5f9', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '14px' }}>Q{idx + 1}</div>
                              <div>
                                <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  {effectiveMode === 'counsellor' ? 'AI Counsellor' : 'AI Interviewer'}
                                </p>
                                <p style={{ margin: 0, fontSize: '15px', color: '#0f172a', fontWeight: '600', lineHeight: '1.5' }}>{qa.question}</p>
                              </div>
                            </div>
                            
                            <div style={{ display: 'flex', gap: '16px', marginLeft: '16px', paddingLeft: '16px', borderLeft: '2px solid #e2e8f0' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                  <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Your Answer</p>
                                  {effectiveMode !== 'counsellor' && qa.score !== undefined && (
                                    <div style={{ background: qa.score >= 80 ? '#dcfce7' : qa.score >= 60 ? '#fef9c3' : '#fee2e2', color: qa.score >= 80 ? '#16a34a' : qa.score >= 60 ? '#ca8a04' : '#dc2626', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '800' }}>
                                      Score: {qa.score}/100
                                    </div>
                                  )}
                                </div>
                                <p style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#334155', lineHeight: '1.6', fontStyle: 'italic' }}>"{qa.answer}"</p>
                                
                                <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                  <p style={{ margin: '0 0 6px 0', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Sparkles size={14} /> AI Feedback
                                  </p>
                                  <p style={{ margin: 0, fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>{qa.feedback}</p>
                                </div>

                                {(qa.ideal_answer || qa.correct_answer || qa.recommended_answer) && (
                                  <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '12px', border: '1px solid #bbf7d0', marginTop: '12px' }}>
                                    <p style={{ margin: '0 0 6px 0', fontSize: '12px', fontWeight: '700', color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <CheckCircle2 size={14} /> Correct / Model Answer (What You Should Have Answered)
                                    </p>
                                    <p style={{ margin: 0, fontSize: '14px', color: '#166534', lineHeight: '1.6', fontWeight: '500' }}>
                                      {qa.ideal_answer || qa.correct_answer || qa.recommended_answer}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* TRANSCRIPT */}
                {reportData.report.transcript && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                      <div style={{ background: 'linear-gradient(135deg, #64748b, #475569)', color: '#ffffff', padding: '10px', borderRadius: '12px', boxShadow: '0 8px 16px rgba(100,116,139,0.2)' }}>
                        <FileText size={20} />
                      </div>
                      <h4 style={{ margin: 0, fontSize: '20px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.3px' }}>Session Transcript</h4>
                    </div>
                    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '24px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)', maxHeight: '400px', overflowY: 'auto' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {reportData.report.transcript.split('\n').filter(line => line.trim()).map((line, idx) => {
                          const isAI = line.startsWith('[AI');
                          return (
                            <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: isAI ? 'flex-start' : 'flex-end', width: '100%' }}>
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {isAI ? (effectiveMode === 'counsellor' ? 'AI Counsellor' : 'AI Interviewer') : 'You'}
                              </span>
                              <div style={{ 
                                background: isAI ? '#f1f5f9' : '#3b82f6', 
                                color: isAI ? '#334155' : '#ffffff', 
                                padding: '12px 18px', 
                                borderRadius: isAI ? '2px 16px 16px 16px' : '16px 2px 16px 16px',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                maxWidth: '85%'
                              }}>
                                {line.replace(/\[.*?\]\s*/, '')}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

              </div>
              </div>
            </div>

          </div>
        </div>,
        document.body
      )}
      {renderHistoryModal()}
    </>
  );
};

export default PerformanceReport;

